import type { AlunarUpgradeId, AlunarUpgradeOffer, PaidReading } from '../types';
import { toPaidReadingPresentation } from '../modules/paid-reading-presentation';
import {
  ALUNAR_ASTRO_SOCIALS,
  ALUNAR_ASTRO_WEBSITE,
  createPaidReadingPdfCoordinator,
  isPaidReadingPdfEligible,
  PAID_READING_PDF_IDLE_LABEL,
  savePaidReadingAsPdf,
  type PaidReadingPdfActionState,
  type PaidReadingPdfSaver,
} from '../modules/paid-reading-pdf';
import { element, registerCleanup } from './dom';
import { testimonialSubmissionView, type TestimonialSubmissionActions } from './testimonial-submission';

interface PaidPdfActions {
  top: HTMLElement;
  bottom: HTMLElement;
  sticky: HTMLElement;
  activate: () => void;
}

export interface PaidUpgradeActions {
  offers: AlunarUpgradeOffer[];
  onUpgrade: (upgradeId: AlunarUpgradeId) => Promise<void>;
}

export interface PaidUpgradePresentation {
  title: string;
  subtitle: string;
  supportingCopy: string;
  normalIncrement: string;
  upgradePrice: string;
  buttonLabel: string;
  hero: boolean;
  badge?: 'BEST VALUE';
}

export function paidResultView(reading: PaidReading, savePdf: PaidReadingPdfSaver = savePaidReadingAsPdf, onPdfSaved?: () => void, upgrades?: PaidUpgradeActions, testimonial?: TestimonialSubmissionActions): HTMLElement {
  const view = toPaidReadingPresentation(reading);
  const expanded = view.sourceVersion === 'paid-v3';
  const section = element('section', { className: 'screen result paid-result', attrs: { 'aria-labelledby': 'paid-title' } });
  section.dataset.celestialMode='funnel';
  const shell = element('div', { className: 'shell paid-shell' });
  const header = element('header', { className: 'result-crown paid-crown mask-reading' });
  header.append(
    element('div', { className: 'paid-sigil', text: '✦', attrs: { 'aria-hidden': 'true' } }),
    element('p', { className: 'result-kicker', text: view.forecast.length === 6 ? 'Your Complete Six-Month Reading' : `Your Complete ${view.forecast.length}-Month Reading` }),
    element('h1', { className: 'result-title', text: view.title, attrs: { id: 'paid-title' } }),
    element('p', { className: 'prepared-for', text: `Prepared for ${view.preparedFor}` }),
  );
  if (view.personalizedSubtitle) header.append(element('p', { className: 'paid-subtitle', text: view.personalizedSubtitle }));
  if (view.badges.length) {
    const badges = element('div', { className: 'seals paid-seals', attrs: { 'aria-label': 'Astrology and numerology summary' } });
    view.badges.forEach((badge, index) => badges.append(element('span', { className: `seal ${index % 2 ? 'silver' : 'gold'}`, text: badge })));
    header.append(badges);
  }
  if (view.interpretiveQuote) header.append(element('blockquote', { className: 'paid-hero-quote', text: view.interpretiveQuote }));
  shell.append(header);

  const pdfActions = isPaidReadingPdfEligible(reading) ? createPdfActions(section, reading, savePdf, onPdfSaved) : undefined;
  if (pdfActions) shell.append(pdfActions.top);

  if (view.readingAtAGlance) {
    const glance = element('section', { className: 'reading-at-glance mask-reading', attrs: { 'aria-labelledby': 'reading-glance-title' } });
    glance.append(element('h2', { text: 'Your Reading at a Glance', attrs: { id: 'reading-glance-title' } }));
    const grid = element('div', { className: 'glance-grid' });
    view.readingAtAGlance.forEach((item) => grid.append(feature(item.label, item.text, 'glance')));
    glance.append(grid);
    shell.append(glance);
  }

  const signature = element('article', { className: 'reading-card paid-chapter mask-reading' });
  signature.append(chapterHeading('I', 'Your Celestial Signature'), element('p', { className: 'chapter-overview', text: view.celestialSignature.overview }));
  const planetary = element('div', { className: 'planet-grid' });
  (['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn'] as const).forEach((key) => {
    const item = element('section', { className: 'planet-card' });
    item.append(element('p', { className: 'planet-label', text: key }), element('p', { text: view.celestialSignature[key] }));
    planetary.append(item);
  });
  signature.append(planetary);
  const numerology = view.celestialSignature.numerology;
  const numbers = element('section', { className: 'numerology-card' });
  numbers.append(element('h3', { text: 'Your Numerology' }), element('p', { text: numerology.overview }));
  const numberGrid = element('div', { className: 'number-grid' });
  [['Life Path', numerology.life_path], ['Birthday Number', numerology.birthday_number], ['Personal Year', numerology.personal_year]].forEach(([label, copy]) => {
    const item = element('div');
    item.append(element('span', { text: label }), element('p', { text: copy }));
    numberGrid.append(item);
  });
  numbers.append(numberGrid);
  signature.append(numbers);
  shell.append(signature);

  if (view.corePattern) shell.append(areaChapter('II', 'Your Core Pattern', view.corePattern.overview, view.corePattern.details.map(({ label, text }) => [label, text])));
  shell.append(
    areaChapter(expanded ? 'III' : 'II', 'Love & Relationships', view.loveAndRelationships.overview, view.loveAndRelationships.details.map(({ label, text }) => [label, text])),
    areaChapter(expanded ? 'IV' : 'III', 'Career & Money', view.careerAndMoney.overview, view.careerAndMoney.details.map(({ label, text }) => [label, text])),
    areaChapter(expanded ? 'V' : 'IV', 'Purpose & Growth', view.purposeAndGrowth.overview, view.purposeAndGrowth.details.map(({ label, text }) => [label, text])),
  );

  const forecastIntro = element('header', { className: 'forecast-intro' });
  forecastIntro.append(
    element('p', { className: 'chapter-number', text: expanded ? 'VI' : 'V' }),
    element('h2', { text: view.forecast.length === 6 ? 'Your Next Six Months' : `Your Next ${view.forecast.length} Months` }),
    element('p', { text: 'A month-by-month guide to the themes, openings, and choices ahead.' }),
  );
  shell.append(forecastIntro);
  const forecastGrid = element('div', { className: 'forecast-grid' });
  view.forecast.forEach((forecast, index) => {
    const calendarMonth = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${forecast.month}-01T00:00:00.000Z`)).toUpperCase();
    const month = element('article', { className: 'month-card mask-reading' });
    month.append(
      element('p', { className: 'month-label', text: `MONTH ${index + 1} OF ${view.forecast.length} · ${calendarMonth}` }),
      element('p', { className: 'personal-month', text: `Personal Month ${forecast.personalMonth}` }),
      element('h3', { text: forecast.theme }),
    );
    if (forecast.whyThisMonthFeelsDifferent) month.append(forecastPanel('Why This Month Feels Different', forecast.whyThisMonthFeelsDifferent, 'why'));
    const opportunityChallenge = element('div', { className: 'month-pair' });
    opportunityChallenge.append(forecastPanel('Opportunity', forecast.opportunity, 'opportunity'), forecastPanel('Challenge', forecast.challenge, 'challenge'));
    const editorial = element('div', { className: 'month-editorial' });
    editorial.append(forecastField('Love & Relationships', forecast.loveAndRelationships), forecastField('Career & Money', forecast.careerAndMoney));
    const close = element('div', { className: 'month-closing' });
    close.append(forecastField('Practical Step', forecast.practicalStep), forecastField('Reflection', forecast.reflection), element('blockquote', { text: forecast.affirmation }));
    month.append(opportunityChallenge, editorial, close);
    forecastGrid.append(month);
  });
  shell.append(forecastGrid);

  if (view.majorTurningPoint) {
    const turning = element('article', { className: 'reading-card paid-chapter turning-point mask-reading' });
    const month = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${view.majorTurningPoint.month}-01T00:00:00.000Z`));
    turning.append(chapterHeading('VII', 'Your Major Turning Point'), element('p', { className: 'turning-month', text: month }), element('h3', { className: 'turning-title', text: view.majorTurningPoint.title }));
    view.majorTurningPoint.details.forEach((detail) => turning.append(forecastPanel(detail.label, detail.text, 'turning')));
    shell.append(turning);
  }
  if (view.sixMonthPattern) shell.append(areaChapter('VIII', view.forecast.length === 6 ? 'The Pattern Across Your Six Months' : `The Pattern Across Your ${view.forecast.length}-Month Horizon`, view.sixMonthPattern.overview, view.sixMonthPattern.details.map(({ label, text }) => [label, text])));

  const closing = element('section', { className: 'closing-card mask-reading' });
  closing.append(
    element('span', { text: '✦', attrs: { 'aria-hidden': 'true' } }),
    ...(expanded ? [element('p', { className: 'chapter-number', text: 'IX' })] : []),
    element('h2', { text: 'A Message for the Path Ahead' }),
    element('h3', { className: 'path-ahead-title', text: view.pathAhead.title }),
    element('p', { className: 'path-ahead-message', text: view.pathAhead.message }),
  );
  if (view.pathAhead.closingQuote) closing.append(element('blockquote', { text: view.pathAhead.closingQuote }));
  shell.append(closing);
  if (pdfActions) shell.append(pdfActions.bottom);
  if (upgrades?.offers.length) shell.append(paidUpgradeSection(upgrades));
  if (testimonial) shell.append(testimonialSubmissionView(testimonial));
  shell.append(followArcana(), element('p', { className: 'result-disclaimer', text: 'For entertainment and self-discovery purposes only. Not medical, psychological, legal, or financial advice.' }));

  section.append(shell);
  if (pdfActions) {
    section.append(pdfActions.sticky);
    pdfActions.activate();
  }
  return section;
}

export function paidUpgradeOptions(offers: AlunarUpgradeOffer[]): AlunarUpgradeOffer[] { return offers.map((offer) => ({ ...offer })); }

export function paidUpgradePresentation(offer: AlunarUpgradeOffer): PaidUpgradePresentation {
  const upgradePrice = `$${(offer.price_cents / 100).toFixed(2)}`;
  if (offer.upgrade_id === 'alunar_upgrade_3m_to_6m') {
    return {
      title: 'Continue to 6 Months',
      subtitle: 'Your next chapter',
      supportingCopy: 'Add 3 more months to your reading',
      normalIncrement: '$5.00',
      upgradePrice,
      buttonLabel: 'Continue to 6 Months',
      hero: false,
    };
  }
  return {
    title: 'Unlock Your Full Year',
    subtitle: 'See the bigger pattern',
    supportingCopy: 'Extend your reading to the full 12 months',
    normalIncrement: offer.from_package_id === 'alunar_3m' ? '$10.00' : '$5.00',
    upgradePrice,
    buttonLabel: 'Unlock Your Full Year',
    hero: true,
    badge: 'BEST VALUE',
  };
}

function paidUpgradeSection(actions: PaidUpgradeActions): HTMLElement {
  const offers = paidUpgradeOptions(actions.offers);
  const currentMonths = offers[0]?.from_package_id === 'alunar_3m' ? 3 : 6;
  const section = element('section', { className: `paid-upgrade paid-upgrade-${currentMonths}m`, attrs: { 'aria-labelledby': 'paid-upgrade-title' } });
  section.append(
    element('p', { className: 'result-kicker paid-upgrade-eyebrow', text: 'CONTINUE YOUR JOURNEY' }),
    element('h2', { text: currentMonths === 3 ? 'Your Story Doesn’t End Here' : 'SEE THE REST OF YOUR YEAR', attrs: { id: 'paid-upgrade-title' } }),
    element('p', {
      className: 'paid-upgrade-intro',
      text: currentMonths === 3
        ? 'You’ve uncovered the next 3 months. Continue following the patterns already beginning to unfold.'
        : 'You’ve uncovered the next 6 months. Continue following the patterns already beginning to unfold.',
    }),
  );
  const path = element('ol', { className: 'paid-upgrade-path', attrs: { 'aria-label': 'Reading journey progress' } });
  const pathMonths = currentMonths === 3 ? [3, 6, 12] : [6, 12];
  pathMonths.forEach((months, index) => {
    const item = element('li', {
      className: index === 0 ? 'unlocked' : 'available',
      text: index === 0 ? `✓ ${months} Months Unlocked` : `${months} Months`,
    });
    if (index < pathMonths.length - 1) item.append(element('span', { className: 'paid-upgrade-path-arrow', text: '→', attrs: { 'aria-hidden': 'true' } }));
    path.append(item);
  });
  section.append(path);
  const grid = element('div', { className: `paid-upgrade-grid${offers.length === 1 ? ' single' : ''}` });
  const error = element('p', { className: 'error paid-upgrade-error', attrs: { role: 'alert', 'aria-live': 'polite' } });
  const buttons: HTMLButtonElement[] = [];
  offers.forEach((offer) => {
    const presentation = paidUpgradePresentation(offer);
    const card = element('article', { className: `paid-upgrade-card${presentation.hero ? ' hero' : ''}` });
    if (presentation.badge) card.append(element('span', { className: 'paid-upgrade-badge', text: presentation.badge }));
    card.append(
      element('span', { className: 'paid-upgrade-glyph', text: presentation.hero ? '☾' : '✦', attrs: { 'aria-hidden': 'true' } }),
      element('h3', { text: presentation.title }),
      element('p', { className: 'paid-upgrade-subtitle', text: presentation.subtitle }),
      element('p', { className: 'paid-upgrade-normal', text: 'Normal incremental value: ' }),
    );
    card.querySelector('.paid-upgrade-normal')?.append(element('s', { text: presentation.normalIncrement }));
    const price = element('p', { className: 'paid-upgrade-price' });
    price.append(element('span', { text: 'Upgrade today for' }), element('strong', { text: presentation.upgradePrice }));
    card.append(price, element('p', { className: 'paid-upgrade-support', text: presentation.supportingCopy }));
    const button = element('button', { className: 'secondary paid-upgrade-button', text: presentation.buttonLabel, attrs: { type: 'button' } });
    buttons.push(button);
    button.addEventListener('click', () => {
      buttons.forEach((item) => { item.disabled = true; });
      button.textContent = 'Opening Secure Checkout…';
      error.textContent = '';
      void actions.onUpgrade(offer.upgrade_id).catch((caught) => {
        buttons.forEach((item) => { item.disabled = false; });
        button.textContent = presentation.buttonLabel;
        error.textContent = caught instanceof Error ? caught.message : 'Please try again.';
      });
    });
    card.append(button);
    grid.append(card);
  });
  section.append(
    grid,
    element('p', { className: 'paid-upgrade-reassurance', text: 'One-time payment · Your existing reading is preserved' }),
    error,
  );
  return section;
}

function chapterHeading(number: string, title: string): HTMLElement {
  const header = element('header', { className: 'chapter-heading' });
  header.append(element('p', { className: 'chapter-number', text: number }), element('h2', { text: title }));
  return header;
}

function areaChapter(number: string, title: string, overview: string, details: Array<[string, string]>): HTMLElement {
  const card = element('article', { className: 'reading-card paid-chapter mask-reading' });
  card.append(chapterHeading(number, title), element('p', { className: 'chapter-overview', text: overview }));
  if (details.length) {
    const grid = element('div', { className: 'polarity-grid' });
    details.forEach(([label, copy], index) => grid.append(feature(label, copy, index ? 'growth' : 'strength')));
    card.append(grid);
  }
  return card;
}

function feature(title: string, copy: string, className: string): HTMLElement {
  const block = element('section', { className: `feature-block ${className}` });
  const text = element('div');
  text.append(element('h3', { text: title }), element('p', { text: copy }));
  block.append(element('span', { className: 'feature-glyph', text: className === 'growth' || className === 'challenge' ? '△' : '◇', attrs: { 'aria-hidden': 'true' } }), text);
  return block;
}

function forecastField(label: string, copy: string): HTMLElement {
  const field = element('p', { className: 'forecast-field' });
  field.append(element('strong', { text: `${label}: ` }), document.createTextNode(copy));
  return field;
}

function forecastPanel(label: string, copy: string, className: string): HTMLElement {
  const panel = element('section', { className: `month-panel ${className}` });
  panel.append(element('h4', { text: label }), element('p', { text: copy }));
  return panel;
}

function followArcana(): HTMLElement {
  const follow = element('footer', { className: 'paid-follow', attrs: { 'aria-labelledby': 'paid-follow-title' } });
  follow.append(
    element('span', { className: 'paid-follow-ornament', text: '✦', attrs: { 'aria-hidden': 'true' } }),
    element('p', { className: 'paid-follow-brand', text: 'ALUNAR ASTRO' }),
    externalLink('alunarastro.com', ALUNAR_ASTRO_WEBSITE, 'paid-follow-site'),
    element('h2', { text: 'Follow Alunar Astro', attrs: { id: 'paid-follow-title' } }),
  );
  const links = element('div', { className: 'paid-follow-links' });
  ALUNAR_ASTRO_SOCIALS.forEach((social) => {
    const link = externalLink(`${social.platform} · ${social.label}`, social.url, 'paid-follow-link');
    link.prepend(element('span', { className: `paid-follow-icon ${social.platform.toLowerCase()}`, text: social.platform === 'Instagram' ? '◎' : 'f', attrs: { 'aria-hidden': 'true' } }));
    links.append(link);
  });
  follow.append(links);
  return follow;
}

function externalLink(label: string, href: string, className: string): HTMLAnchorElement {
  return element('a', { className, text: label, attrs: { href, target: '_blank', rel: 'noopener noreferrer' } });
}

function createPdfActions(root: HTMLElement, reading: PaidReading, savePdf: PaidReadingPdfSaver, onSaved?: () => void): PaidPdfActions {
  const buttons: HTMLButtonElement[] = [];
  const errors: HTMLElement[] = [];
  const makeAction = (placement: 'top' | 'bottom' | 'sticky'): HTMLElement => {
    const wrap = element(placement === 'sticky' ? 'aside' : 'div', {
      className: `paid-pdf-action paid-pdf-${placement}${placement === 'sticky' ? ' hidden' : ''}`,
      attrs: placement === 'sticky' ? { 'aria-label': 'Save your paid reading' } : {},
    });
    const button = element('button', { className: 'secondary paid-pdf-button', text: PAID_READING_PDF_IDLE_LABEL, attrs: { type: 'button' } });
    const error = element('p', { className: 'error paid-pdf-error', attrs: { role: 'alert', 'aria-live': 'polite' } });
    buttons.push(button);
    errors.push(error);
    wrap.append(button, error);
    return wrap;
  };

  const top = makeAction('top');
  const bottom = makeAction('bottom');
  const sticky = makeAction('sticky');
  const update = (state: PaidReadingPdfActionState): void => {
    buttons.forEach((button) => {
      button.textContent = state.label;
      button.disabled = state.disabled;
      button.setAttribute('aria-busy', String(state.disabled));
    });
    errors.forEach((error) => { error.textContent = state.error; });
  };
  const coordinator = createPaidReadingPdfCoordinator(reading, async(value)=>{await savePdf(value);onSaved?.()}, update);
  buttons.forEach((button) => button.addEventListener('click', () => { void coordinator.run(); }));

  const activate = (): void => {
    const inViewport = (node: HTMLElement): boolean => {
      const rect = node.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    };
    const upgrade = root.querySelector<HTMLElement>('.paid-upgrade');
    const observed: HTMLElement[] = [top, bottom];
    if (upgrade) observed.push(upgrade);
    const visibility = new Map<Element, boolean>(observed.map((node) => [node, inViewport(node)]));
    const overlayOpen = (): boolean => Boolean(document.querySelector('.consent, .privacy-modal'));
    const refresh = (): void => {
      const stickyBlockerVisible = Array.from(visibility.values()).some(Boolean);
      sticky.classList.toggle('hidden', stickyBlockerVisible || overlayOpen());
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => visibility.set(entry.target, entry.isIntersecting));
        refresh();
      }, { threshold: 0 });
      observed.forEach((node) => observer.observe(node));
      registerCleanup(root, () => observer.disconnect());
    }
    const mutation = new MutationObserver(refresh);
    mutation.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
    registerCleanup(root, () => mutation.disconnect());
    refresh();
  };
  return { top, bottom, sticky, activate };
}
